(function () {
  "use strict";

  const AGE_MAP = { under_35: 31, "35_44": 40, "45_54": 50, "55_64": 59, over_65: 67 };
  const AGE_ORDER = ["under_35", "35_44", "45_54", "55_64", "over_65"];

  // Only used if a problem_areas step exists (TSL has none). Kept harmless.
  const PROBLEM_LABELS = {};

  const FRUSTRATION_LABELS = {
    two_day_hangovers: "two-day hangovers",
    afternoon_crash: "the afternoon crash",
    brain_fog: "brain fog",
    stubborn_gut: "a stubborn midsection",
    liver_enzymes: "flagged liver enzymes",
    fatty_liver: "a fatty liver diagnosis",
  };

  const LIFE_CHANGE_LABELS = {
    night_out_no_cost: "enjoying a night out without paying for it",
    afternoon_no_crash: "getting through the afternoon without crashing",
    think_clearly: "thinking clearly again",
    liver_numbers_in_range: "getting liver numbers back in range",
    sleep_deep_rested: "sleeping deep and waking up rested",
    feel_like_myself: "feeling like myself again"
  };

  const LIFE_CHANGE_SCORE_WEIGHT = {
    night_out_no_cost: 11,
    afternoon_no_crash: 10,
    think_clearly: 11,
    liver_numbers_in_range: 13,
    sleep_deep_rested: 12,
    feel_like_myself: 11
  };

  class CroevQuiz {
    constructor(container) {
      this.container = container;
      this.sectionId = container.dataset.sectionId;
      this.redirectUrl = container.dataset.redirectUrl;
      const dataEl = document.getElementById(`croev-quiz-data-${this.sectionId}`);
      this.config = dataEl ? JSON.parse(dataEl.textContent) : {};
      this.state = { currentStep: 0, answers: {}, history: [] };
      this.steps = Array.from(container.querySelectorAll(".croev-quiz__step"));
      this.progressBar = container.querySelector(".croev-quiz__progress-bar");
      this.progressContainer = container.querySelector(".croev-quiz__progress");
      this.autoAdvanceTimer = null;
      this.checklistTimers = [];
      this.init();
    }

    init() {
      this.clearState();
      this.bindEvents();
      this.showStep(this.state.currentStep);
      this.updateProgress();
    }

    bindEvents() {
      this.container.addEventListener("click", (e) => {
        const option = e.target.closest(".croev-quiz__option");
        if (option) this.handleOptionSelect(option);
        if (e.target.closest('[data-action="continue"]')) this.nextStep();
        if (e.target.closest('[data-action="back"]')) this.prevStep();
        const claimBtn = e.target.closest('[data-action="claim-offer"]');
        if (claimBtn) this.handleClaimOffer(claimBtn);
        const skipBtn = e.target.closest('[data-action="skip"]');
        if (skipBtn) this.handleSkip(skipBtn);
        const unitBtn = e.target.closest(".croev-quiz__unit-btn");
        if (unitBtn) this.handleUnitToggle(unitBtn);
      });

      this.container.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          const option = e.target.closest(".croev-quiz__option");
          if (option) {
            e.preventDefault();
            this.handleOptionSelect(option);
          }
        }
      });

      this.container.addEventListener("input", (e) => {
        const input = e.target.closest(".croev-quiz__input");
        if (input) this.handleInputChange(input);
        const measInput = e.target.closest(".croev-quiz__measurement-input");
        if (measInput) this.handleMeasurementChange(measInput);
      });

      this.container.addEventListener("keydown", (e) => {
        if (e.key === "Enter") {
          const input =
            e.target.closest(".croev-quiz__input") ||
            e.target.closest(".croev-quiz__measurement-input");
          if (input) {
            const continueBtn = input
              .closest(".croev-quiz__step")
              ?.querySelector('[data-action="continue"]');
            if (continueBtn && !continueBtn.disabled) this.nextStep();
          }
        }
      });
    }

    handleInputChange(input) {
      const step = input.closest(".croev-quiz__step");
      const questionId = step.dataset.questionId;
      const value = input.value.trim();
      const inputType = input.type;
      const min = input.min ? parseInt(input.min, 10) : null;
      const max = input.max ? parseInt(input.max, 10) : null;
      const required = input.hasAttribute("required");
      let isValid = true;
      const errorEl = step.querySelector(".croev-quiz__input-error");

      if (required && !value) {
        isValid = false;
      } else if (inputType === "number" && value) {
        const numVal = parseInt(value, 10);
        if (isNaN(numVal) || (min !== null && numVal < min) || (max !== null && numVal > max)) {
          isValid = false;
        }
      } else if (inputType === "email" && value) {
        isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      }

      input.classList.toggle("is-invalid", !isValid && value.length > 0);
      if (errorEl) errorEl.hidden = isValid || value.length === 0;

      if (isValid && value) {
        this.state.answers[questionId] = { value, label: value };
        this.saveState();
      } else {
        delete this.state.answers[questionId];
      }

      const continueBtn = step.querySelector('[data-action="continue"]');
      if (continueBtn) continueBtn.disabled = !isValid || !value;
    }

    handleUnitToggle(btn) {
      const toggle = btn.closest(".croev-quiz__unit-toggle");
      const step = btn.closest(".croev-quiz__step");
      const unit = btn.dataset.unit;
      const wrapper = step.querySelector(".croev-quiz__measurement-wrapper");
      toggle.querySelectorAll(".croev-quiz__unit-btn").forEach((b) => b.classList.remove("is-active"));
      btn.classList.add("is-active");
      wrapper.dataset.activeUnit = unit;
      wrapper.querySelectorAll(".croev-quiz__measurement-fields").forEach((group) => {
        group.hidden = group.dataset.unitGroup !== unit;
      });
      wrapper
        .querySelectorAll(".croev-quiz__measurement-fields[hidden] .croev-quiz__measurement-input")
        .forEach((input) => {
          input.value = "";
        });
      const visibleInput = wrapper.querySelector(
        ".croev-quiz__measurement-fields:not([hidden]) .croev-quiz__measurement-input"
      );
      if (visibleInput) {
        this.handleMeasurementChange(visibleInput);
      } else {
        const continueBtn = step.querySelector('[data-action="continue"]');
        if (continueBtn) continueBtn.disabled = true;
      }
    }

    handleMeasurementChange(input) {
      const step = input.closest(".croev-quiz__step");
      const questionId = step.dataset.questionId;
      const measurementType = step.dataset.measurementType;
      const wrapper = step.querySelector(".croev-quiz__measurement-wrapper");
      const activeUnit = wrapper.dataset.activeUnit;
      const fields = wrapper.querySelector(
        `.croev-quiz__measurement-fields[data-unit-group="${activeUnit}"]`
      );
      const inputs = fields.querySelectorAll(".croev-quiz__measurement-input");
      const errorEl = step.querySelector(".croev-quiz__input-error");
      const continueBtn = step.querySelector('[data-action="continue"]');
      let isValid = true;
      let displayValue = "";
      let rawValue = "";

      if (measurementType === "height" && activeUnit === "imperial") {
        const feetInput = fields.querySelector('[data-measurement="feet"]');
        const inchesInput = fields.querySelector('[data-measurement="inches"]');
        const feet = feetInput.value.trim();
        const inches = inchesInput.value.trim();
        if (!feet && !inches) {
          isValid = false;
        } else {
          const feetNum = parseInt(feet, 10);
          const inchesNum = parseInt(inches || "0", 10);
          if (feet && (isNaN(feetNum) || feetNum < 3 || feetNum > 8)) isValid = false;
          if (inches && (isNaN(inchesNum) || inchesNum < 0 || inchesNum > 11)) isValid = false;
          if (!feet) isValid = false;
          if (isValid) {
            displayValue = `${feetNum}'${inchesNum}"`;
            rawValue = `${feetNum}ft ${inchesNum}in`;
          }
        }
      } else {
        const singleInput = inputs[0];
        const val = singleInput.value.trim();
        const min = parseInt(singleInput.min, 10);
        const max = parseInt(singleInput.max, 10);
        if (!val) {
          isValid = false;
        } else {
          const numVal = parseInt(val, 10);
          if (isNaN(numVal) || numVal < min || numVal > max) {
            isValid = false;
          } else {
            const unit = singleInput.dataset.measurement;
            displayValue = `${numVal} ${unit}`;
            rawValue = `${numVal} ${unit}`;
          }
        }
      }

      if (errorEl) errorEl.hidden = isValid || !this.hasMeasurementInput(fields);
      if (isValid) {
        this.state.answers[questionId] = { value: rawValue, label: displayValue, unit: activeUnit };
        this.saveState();
      } else {
        delete this.state.answers[questionId];
      }
      if (continueBtn) continueBtn.disabled = !isValid;
    }

    hasMeasurementInput(fields) {
      return Array.from(fields.querySelectorAll(".croev-quiz__measurement-input")).some(
        (i) => i.value.trim().length > 0
      );
    }

    handleOptionSelect(option) {
      const step = option.closest(".croev-quiz__step");
      const questionId = step.dataset.questionId;
      const selectionType = step.dataset.selectionType;
      const autoAdvance = step.dataset.autoAdvance === "true";
      const value = option.dataset.optionValue;
      const label = option.dataset.optionLabel;

      if (selectionType === "single") {
        step.querySelectorAll(".croev-quiz__option").forEach((opt) => {
          opt.classList.remove("is-selected");
          opt.setAttribute("aria-pressed", "false");
        });
        option.classList.add("is-selected");
        option.setAttribute("aria-pressed", "true");
        this.state.answers[questionId] = { value, label };
        this.saveState();
        if (autoAdvance) {
          setTimeout(() => this.nextStep(), 300);
        } else {
          const continueBtn = step.querySelector('[data-action="continue"]');
          if (continueBtn) continueBtn.disabled = false;
        }
      } else {
        option.classList.toggle("is-selected");
        const isSelected = option.classList.contains("is-selected");
        option.setAttribute("aria-pressed", isSelected ? "true" : "false");
        const selectedOptions = Array.from(step.querySelectorAll(".croev-quiz__option.is-selected"));
        this.state.answers[questionId] = selectedOptions.map((opt) => ({
          value: opt.dataset.optionValue,
          label: opt.dataset.optionLabel
        }));
        this.saveState();
        const continueBtn = step.querySelector('[data-action="continue"]');
        if (continueBtn) continueBtn.disabled = selectedOptions.length === 0;
      }
    }

    showStep(index) {
      this.clearTimers();
      this.steps.forEach((step, i) => {
        if (i === index) {
          step.classList.add("is-active");
          step.classList.remove("is-exiting");
        } else {
          step.classList.remove("is-active");
        }
      });
      const currentStep = this.steps[index];
      if (!currentStep) return;
      const stepType = currentStep.dataset.stepType;

      setTimeout(() => {
        const inputField = currentStep.querySelector(".croev-quiz__input");
        if (inputField) inputField.focus();
      }, 100);

      if (stepType === "question_image" || stepType === "question_text") this.restoreSelections(currentStep);
      if (stepType === "question_input") this.restoreInputValue(currentStep);
      if (stepType === "summary_profile") this.populateSummaryProfile(currentStep);
      if (stepType === "summary_results") this.populateSummaryResults(currentStep);
      if (stepType === "summary_comparison") this.populateSummaryComparison(currentStep);
      if (stepType === "loading_step") this.handleLoadingStep(currentStep);
      if (stepType === "info_step") this.populateInfoStep(currentStep);
      if (stepType === "offer_step") {
        this.populateOfferStep(currentStep);
        this.initOfferCarousel(currentStep);
      }

this.updateProgress();
      const wrapper = this.container.querySelector(".croev-quiz__wrapper");
      if (wrapper) wrapper.scrollTop = 0;
      window.scrollTo({ top: 0, behavior: "smooth" });
    }

    restoreSelections(step) {
      const questionId = step.dataset.questionId;
      const answer = this.state.answers[questionId];
      if (!answer) return;
      const values = step.dataset.selectionType === "multi" ? answer.map((a) => a.value) : [answer.value];
      step.querySelectorAll(".croev-quiz__option").forEach((option) => {
        const optValue = option.dataset.optionValue;
        if (values.includes(optValue)) {
          option.classList.add("is-selected");
          option.setAttribute("aria-pressed", "true");
        }
      });
      const continueBtn = step.querySelector('[data-action="continue"]');
      if (continueBtn && values.length > 0) continueBtn.disabled = false;
    }

    restoreInputValue(step) {
      const questionId = step.dataset.questionId;
      const answer = this.state.answers[questionId];
      const measurementType = step.dataset.measurementType;

      if (measurementType && answer) {
        const unit = answer.unit || "imperial";
        const wrapper = step.querySelector(".croev-quiz__measurement-wrapper");
        const toggle = step.querySelector(".croev-quiz__unit-toggle");
        if (toggle) {
          toggle.querySelectorAll(".croev-quiz__unit-btn").forEach((btn) => {
            btn.classList.toggle("is-active", btn.dataset.unit === unit);
          });
        }
        if (wrapper) {
          wrapper.dataset.activeUnit = unit;
          wrapper.querySelectorAll(".croev-quiz__measurement-fields").forEach((group) => {
            group.hidden = group.dataset.unitGroup !== unit;
          });
          const rawValue = answer.value || "";
          const fields = wrapper.querySelector(`.croev-quiz__measurement-fields[data-unit-group="${unit}"]`);
          if (measurementType === "height" && unit === "imperial") {
            const match = rawValue.match(/(\d+)ft\s*(\d+)in/);
            if (match) {
              const feetInput = fields.querySelector('[data-measurement="feet"]');
              const inchesInput = fields.querySelector('[data-measurement="inches"]');
              if (feetInput) feetInput.value = match[1];
              if (inchesInput) inchesInput.value = match[2];
            }
          } else {
            const match = rawValue.match(/(\d+)/);
            if (match && fields) {
              const input2 = fields.querySelector(".croev-quiz__measurement-input");
              if (input2) input2.value = match[1];
            }
          }
          const visibleInput = fields?.querySelector(".croev-quiz__measurement-input");
          if (visibleInput) this.handleMeasurementChange(visibleInput);
        }
        return;
      }

      const input = step.querySelector(".croev-quiz__input");
      if (!answer || !input) return;
      input.value = answer.value || "";
      if (input.value) this.handleInputChange(input);
    }

    nextStep() {
      if (this.state.currentStep < this.steps.length - 1) {
        this.state.history.push(this.state.currentStep);
        this.state.currentStep++;
        while (
          this.state.currentStep < this.steps.length - 1 &&
          this.shouldSkipStep(this.steps[this.state.currentStep])
        ) {
          this.state.currentStep++;
        }
        this.showStep(this.state.currentStep);
        this.updateProgress();
        this.saveState();
      }
    }

    shouldSkipStep(step) {
      const skipIf = step.dataset.skipIf;
      if (!skipIf) return false;
      const [questionId, valuesStr] = skipIf.split(":");
      if (!questionId || !valuesStr) return false;
      const skipValues = valuesStr.split(",").map((v) => v.trim());
      const answer = this.state.answers[questionId];
      if (!answer) return false;
      return (Array.isArray(answer) ? answer.map((a) => a.value) : [answer.value]).some((v) =>
        skipValues.includes(v)
      );
    }

    prevStep() {
      if (this.state.history.length > 0) {
        this.state.currentStep = this.state.history.pop();
        this.showStep(this.state.currentStep);
        this.updateProgress();
        this.saveState();
      }
    }

    updateProgress() {
      const bar =
        this.container.querySelector(".croev-quiz__progress-bar") ||
        document.querySelector(".croev-quiz__progress-bar");
      const wrapper =
        this.container.querySelector(".croev-quiz__progress") ||
        document.querySelector(".croev-quiz__progress");
      if (!bar) return;
      const progress = (this.state.currentStep / Math.max(this.steps.length - 1, 1)) * 100;
      bar.style.setProperty("width", `${progress}%`, "important");
      if (wrapper) wrapper.setAttribute("aria-valuenow", Math.round(progress));
    }

    answerValues(questionId) {
      const q = this.state.answers[questionId];
      if (!q) return [];
      return Array.isArray(q) ? q.map((x) => x.value).filter(Boolean) : q.value ? [q.value] : [];
    }

    getPerceivedAge() {
      const exactAge = this.state.answers.exact_age;
      const ageRangeAnswer = this.state.answers.age;
      let baseAge;
      if (exactAge && exactAge.value) baseAge = parseInt(exactAge.value, 10);
      else if (ageRangeAnswer) baseAge = AGE_MAP[ageRangeAnswer.value] || 45;
      else return null;
      return baseAge + 10;
    }

    getGoalAge() {
      const exactAge = this.state.answers.exact_age;
      const ageRangeAnswer = this.state.answers.age;
      let baseAge;
      if (exactAge && exactAge.value) baseAge = parseInt(exactAge.value, 10);
      else if (ageRangeAnswer) baseAge = AGE_MAP[ageRangeAnswer.value] || 45;
      else return null;
      return baseAge - 8;
    }

    getProblemAreasText(limit = 2) {
      const fr = this.answerValues("frustrations_recent");
      return fr.length > 0
        ? fr.slice(0, limit).map((v) => FRUSTRATION_LABELS[v] || v.replace(/_/g, " ")).join(" and ")
        : "your concerns";
    }

    getDatePlus(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
    }

    populateSummaryProfile(step) {
      const scores = this.calculateTScores();
      this.populateProblemAreasVisual(step);
      this.populateBioAge(step, scores);
      this.populateClinicalSummary(step, scores);
      this.animateDashboard(step, scores);
    }

    /* ----------------------------------------------------------------------
       LIVER SCORING ENGINE
       High score = good (matches the original gauge colours/severity).
       Metrics are capped at MAX_METRIC so the dashboard always reads
       "room to improve", which keeps the funnel's problem framing intact.
       ---------------------------------------------------------------------- */
    calculateTScores() {
      const a = this.state.answers;
      const val = (key) => a[key]?.value || "";
      const vals = (key) => this.answerValues(key);
      const frustr = vals("frustrations_recent");
      const hasFr = (v) => frustr.includes(v);
      const clamp = (n) => Math.max(5, Math.min(95, n));

      // Burden inputs (0-100, higher = more strain on the system)
      const drinkBurden =
        { most_nights: 90, few_times_week: 68, mainly_weekends: 45, socially: 25, dont_drink: 5 }[
          val("drinking_pattern")
        ] ?? 40;
      const painkillerBurden =
        { weekly_or_more: 82, few_times_month: 52, rarely: 18, didnt_know_connected: 45 }[
          val("painkiller_use")
        ] ?? 35;
      const recoveryBurden =
        { costs_whole_weekend: 90, hits_harder: 70, bounce_back_slower: 45, barely_touches_me: 15 }[
          val("recovery_change")
        ] ?? 45;
      const energyBurden =
        { dragging_from_wake: 88, crash_2_3pm: 70, fade_by_evening: 55, energy_holds: 12 }[
          val("energy_pattern")
        ] ?? 50;

      // Age draws on glutathione and NAD+ (both decline with age)
      const ageGlutathione =
        { under_35: 8, "35_44": 18, "45_54": 30, "55_64": 42, over_65: 50 }[val("age")] ?? 25;
      const ageNad =
        { under_35: 10, "35_44": 22, "45_54": 36, "55_64": 48, over_65: 56 }[val("age")] ?? 28;

      // Metric 1: Liver Filtration  (dashboard key: hormonal)
      let filtrationBurden = drinkBurden * 0.4 + painkillerBurden * 0.3 + recoveryBurden * 0.3;
      if (hasFr("liver_enzymes")) filtrationBurden += 12;
      if (hasFr("fatty_liver")) filtrationBurden += 14;
      if (hasFr("two_day_hangovers")) filtrationBurden += 6;
      const hormonal = clamp(Math.round(100 - Math.min(filtrationBurden, 96)));

      // Metric 2: Glutathione Reserve  (dashboard key: lifestyle)
      const toxicLoad = drinkBurden * 0.45 + painkillerBurden * 0.35;
      let glutBurden = toxicLoad * 0.6 + ageGlutathione * 0.5;
      if (hasFr("brain_fog")) glutBurden += 8;
      if (hasFr("not_myself")) glutBurden += 6;
      const tried = vals("already_tried");
      if (tried.includes("nothing_moved_needle")) glutBurden += 8;
      const lifestyle = clamp(Math.round(100 - Math.min(glutBurden, 95)));

      // Metric 3: Cellular Energy / NAD+  (dashboard key: body)
      let nadBurden = energyBurden * 0.55 + ageNad * 0.45;
      if (hasFr("afternoon_crash")) nadBurden += 8;
      if (hasFr("unrefreshing_sleep")) nadBurden += 8;
      const body = clamp(Math.round(100 - Math.min(nadBurden, 95)));

      // Chronological + biological age
      const baseAge =
        { under_35: 31, "35_44": 40, "45_54": 50, "55_64": 59, over_65: 67 }[val("age")] || 45;
      const feelMap = {
        no_older_than_age: 0,
        slight_older_feeling: 5,
        feels_10_years_older: 13,
        running_on_empty: 20
      };
      const feelExtra = Object.prototype.hasOwnProperty.call(feelMap, val("feel_older_than_age"))
        ? feelMap[val("feel_older_than_age")]
        : 0;
      const healthAvg = (hormonal + lifestyle + body) / 3;
      const agePenalty = Math.round((100 - healthAvg) * 0.16 + feelExtra * 0.62);
      const biologicalAge = baseAge + agePenalty;
      const bioageScore = Math.round(
        Math.max(5, Math.min(95, healthAvg * 0.6 + (100 - agePenalty * 2) * 0.4))
      );

      const MAX_METRIC = 58;
      const MIN_AGE_PENALTY = 4;
      const cappedHormonal = Math.min(hormonal, MAX_METRIC);
      const cappedLifestyle = Math.min(lifestyle, MAX_METRIC);
      const cappedBody = Math.min(body, MAX_METRIC);
      const cappedBioage = Math.min(bioageScore, MAX_METRIC);
      const cappedAgePenalty = Math.max(agePenalty, MIN_AGE_PENALTY);
      const cappedBiologicalAge = baseAge + cappedAgePenalty;

      return {
        tscore: Math.min(
          MAX_METRIC,
          Math.round(cappedHormonal * 0.4 + cappedLifestyle * 0.3 + cappedBody * 0.2 + cappedBioage * 0.1)
        ),
        hormonal: cappedHormonal,
        lifestyle: cappedLifestyle,
        body: cappedBody,
        bioage: cappedBioage,
        chronoAge: baseAge,
        biologicalAge: cappedBiologicalAge,
        agePenalty: cappedAgePenalty
      };
    }

getSeverity(score) {
  return score <= 25
    ? { level: "critical", label: "Critical", color: "#c0182b" }
    : score <= 45
    ? { level: "low", label: "Low", color: "#d35400" }
    : score <= 60
    ? { level: "below_avg", label: "Below Avg", color: "#3f74c4" }
    : score <= 78
    ? { level: "average", label: "Average", color: "#1a5fc8" }
    : { level: "optimal", label: "Optimal", color: "#03b983" };
}

    getMetricDetail(key, score) {
      const sev = this.getSeverity(score);
      const details = {
        // Liver Filtration
        hormonal: {
          critical:
            "Your filtration load looks heavy. Alcohol, Tylenol, and years of buildup are stacking up faster than your liver can clear them.",
          low:
            "Your liver is fighting a real backlog right now, which is what tends to show up as slow recovery and that heavy, sluggish feeling.",
          below_avg: "Filtration is running below where it should, so toxins clear slower than they used to.",
          average:
            "Filtration is holding in the mid range. The right support takes pressure off and speeds up how fast you clear the load.",
          optimal: "Your filtration signals look relatively strong, a solid base to build on."
        },
        // Glutathione Reserve
        lifestyle: {
          critical:
            "Your glutathione reserve looks badly depleted. Without it your liver cannot neutralize what you put through it, and the fog and fatigue follow.",
          low: "Your master antioxidant looks run down, which is often the quiet reason energy and clarity slip.",
          below_avg: "Glutathione is sitting lower than ideal, leaving less protection against daily toxic load.",
          average:
            "Your reserve is workable but not topped up. Restocking the raw material it is built from usually changes how you feel within weeks.",
          optimal: "Your glutathione signals look encouraging, which protects energy, clarity, and recovery."
        },
        // Cellular Energy (NAD+)
        body: {
          critical:
            "Your cellular energy looks heavily drained. NAD+ drops sharply with age and load, and that is what an empty tank by mid afternoon feels like.",
          low: "Your NAD+ driven energy looks low, so the same day takes more out of you than it used to.",
          below_avg: "Cellular energy is running under where it should, which shows up as crashes and slow mornings.",
          average: "Energy production is mid range. Recharging NAD+ alongside detox tends to smooth out the dips.",
          optimal: "Your cellular energy signals look solid, a strong base for steady all day output."
        },
        // Biological Age
        bioage: {
          critical:
            "Your answers point to a body aging faster than the calendar, driven by load on the liver and a drained energy system.",
          low: "Several signals suggest your body is running older than your years, which is common and very reversible with the right inputs.",
          below_avg: "A few markers are nudging your biological age above your real one.",
          average: "Your biological age sits close to your real age, with room to pull it back down.",
          optimal: "Your biological age signals look favorable, worth protecting from here."
        }
      };
      return (details[key] && details[key][sev.level]) || "";
    }

    getTScoreDescription(score) {
      return score <= 25
        ? "Your profile points to a liver under real strain, with glutathione and cellular energy both running low. They move together, and they respond fast to the right support."
        : score <= 45
        ? "Your results show meaningful gaps in how well your liver is clearing load and holding energy. Targeted support usually creates quick, visible shifts."
        : score <= 60
        ? "You are sitting below where you probably want to feel, with filtration and energy both a step down. A focused plan can lift the fog and the afternoon crash."
        : score <= 78
        ? "You are closer to balanced than you might feel. A few precise upgrades to how you support your liver and energy can unlock the next level."
        : "Your liver and energy signals look strong. The priority now is maintenance so recovery and clarity stay easy.";
    }

    populateProblemAreasVisual(step) {
      const problemAreasVisual = step.querySelector(".croev-quiz__problem-areas-visual");
      const problemAreas = this.state.answers.problem_areas;
      if (problemAreasVisual && problemAreas && Array.isArray(problemAreas) && problemAreas.length > 0) {
        const selectedAreas = problemAreas.slice(0, 3);
        let html = '<div class="croev-quiz__problem-areas-grid">';
        const imageMap = {};
        selectedAreas.forEach((area) => {
          const imageUrl = imageMap[area.value];
          const label = PROBLEM_LABELS[area.value] || area.label;
          if (imageUrl && imageUrl.length > 10) {
            html += `
              <div class="croev-quiz__problem-area-item">
                <img src="${imageUrl}" alt="${label}" class="croev-quiz__problem-area-img" loading="lazy">
                <span class="croev-quiz__problem-area-label">${label}</span>
              </div>
            `;
          }
        });
        html += "</div>";
        problemAreasVisual.innerHTML = html;
      }
    }

    populateBioAge(step, scores) {
      const chronoEl = step.querySelector('[data-field="chrono_age"]');
      const bioEl = step.querySelector('[data-field="bio_age"]');
      const gapEl = step.querySelector('[data-field="age_gap"]');
      if (chronoEl) chronoEl.textContent = scores.chronoAge;
      if (bioEl) bioEl.textContent = scores.biologicalAge;
      if (gapEl) gapEl.textContent = `+${scores.agePenalty} yrs`;
    }

    populateClinicalSummary(step, scores) {
      const el = step.querySelector('[data-field="clinical_summary"]');
      if (!el) return;
      const existing = el.textContent.trim();
      if (existing && existing !== "--") return;
      const problemAreasText = this.getProblemAreasText();
      const sev = this.getSeverity(scores.tscore);
      el.textContent = `Based on your answers, your liver health score of ${scores.tscore} (${sev.label}) suggests your ${problemAreasText} and daily load are shaping how you feel. Right now your body may be running closer to ${scores.biologicalAge} years old.`;
    }

    animateDashboard(step, scores) {
      const GAUGE_CIRCUMFERENCE = 2 * Math.PI * 70;
      const ANIM_DURATION = 1800;
      const metrics = ["hormonal", "lifestyle", "body", "bioage"];
      const gaugeFill = step.querySelector('[data-gauge="tscore"]');
      const gaugeCounter = step.querySelector('[data-counter="tscore"]');
      const gaugeBadge = step.querySelector('[data-severity="tscore"]');
      const gaugeDesc = step.querySelector('[data-desc="tscore"]');

      if (gaugeFill) {
        const sev = this.getSeverity(scores.tscore);
        const target = GAUGE_CIRCUMFERENCE * (1 - scores.tscore / 100);
        requestAnimationFrame(() => {
          gaugeFill.style.strokeDashoffset = target;
          gaugeFill.style.stroke = sev.color;
        });
        if (gaugeBadge) {
          gaugeBadge.textContent = sev.label;
          gaugeBadge.setAttribute("data-level", sev.level);
        }
        if (gaugeDesc) gaugeDesc.textContent = this.getTScoreDescription(scores.tscore);
      }
      if (gaugeCounter) this.animateCounter(gaugeCounter, scores.tscore, ANIM_DURATION);

      metrics.forEach((key, i) => {
        const score = scores[key];
        const sev = this.getSeverity(score);
        const delay = 600 + i * 150;
        setTimeout(() => {
          const bar = step.querySelector(`[data-bar="${key}"]`);
          const counter = step.querySelector(`[data-counter="${key}"]`);
          const badge = step.querySelector(`[data-severity="${key}"]`);
          const detail = step.querySelector(`[data-detail="${key}"]`);
          if (bar) {
            bar.style.width = `${score}%`;
            bar.style.backgroundColor = sev.color;
          }
          if (counter) this.animateCounter(counter, score, 1400);
          if (badge) {
            badge.textContent = sev.label;
            badge.setAttribute("data-level", sev.level);
          }
          if (detail) detail.textContent = this.getMetricDetail(key, score);
        }, delay);
      });
    }

    animateCounter(el, target, duration) {
      const start = performance.now();
      const from = 0;
      const tick = (now) => {
        const elapsed = now - start;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        el.textContent = Math.round(from + (target - from) * eased);
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }

    populateSummaryResults(step) {
      const answers = this.state.answers;
      const val = (key) => answers[key]?.value || "";
      const label = (key) => answers[key]?.label || "";
      const date30 = this.getDatePlus(30);
      const date60 = this.getDatePlus(60);

      const personalizedEl = step.querySelector("[data-sr-personalized]");
      if (personalizedEl) {
        const concerns = this.getProblemAreasTextBold(3);
        const age = label("age");
        const agePhrase = age ? ` in the <strong>${age}</strong> age group` : "";
        personalizedEl.innerHTML = `You have taken the first real step toward getting your liver and energy back. Based on your profile${agePhrase} dealing with ${concerns}, here is your personalized plan:`;
      }

      const profileGrid = step.querySelector("[data-sr-profile]");
      if (profileGrid) {
        const pills = [];
        const addPill = (lbl, value) => {
          if (value)
            pills.push(
              `<span class="croev-quiz__sr-profile-pill"><span class="croev-quiz__sr-profile-pill-label">${lbl}:</span> <span class="croev-quiz__sr-profile-pill-value">${value}</span></span>`
            );
        };
        addPill("Age", label("age"));
        addPill("Drinking", label("drinking_pattern"));
        addPill("Recovery", label("recovery_change"));
        addPill("Energy dip", label("energy_pattern"));
        addPill("Painkillers", label("painkiller_use"));
        const lifeChVals = this.answerValues("life_change_priority");
        if (lifeChVals.length > 0) {
          const lifeChLabels = lifeChVals.map((v) => LIFE_CHANGE_LABELS[v] || v.replace(/_/g, " "));
          addPill("Top priority", lifeChLabels.join(", "));
        }
        const frustrLabels = this.answerValues("frustrations_recent").map(
          (v) => FRUSTRATION_LABELS[v] || v.replace(/_/g, " ")
        );
        if (frustrLabels.length) addPill("Top frustrations", frustrLabels.slice(0, 4).join(", "));
        profileGrid.innerHTML = pills.join("");
      }

      const d30El = step.querySelector("[data-sr-date-30]");
      const d60El = step.querySelector("[data-sr-date-60]");
      if (d30El) d30El.textContent = date30;
      if (d60El) d60El.textContent = date60;
      step.querySelectorAll("[data-template]").forEach((el) => {
        const template = el.dataset.template;
        el.innerHTML = template.replace("{date_30}", date30).replace("{date_60}", date60);
      });

      const benefitsEl = step.querySelector("[data-sr-benefits]");
      if (benefitsEl) {
        const frustrationBenefitMap = {
          two_day_hangovers:
            "Pre-load your liver before drinking so <strong>one night out stops costing you the weekend</strong>",
          afternoon_crash: "Restore <strong>steady afternoon energy</strong> without leaning on a third coffee",
          brain_fog: "Clear the <strong>brain fog</strong> as your glutathione system comes back online",
          stubborn_gut: "Support the metabolic load sitting behind a <strong>stubborn midsection</strong>",
          liver_enzymes:
            "Give your liver the raw material to push <strong>enzyme levels back toward normal</strong>",
          fatty_liver: "Give your liver the glutathione support it leans on when it's <strong>carrying a heavier load</strong>",
          unrefreshing_sleep: "Help your body move toward <strong>deeper, more restorative sleep</strong>",
          not_myself: "Rebuild your baseline so you <strong>feel like yourself again</strong>"
        };
        const lifeChangeBenefitMap = {
          night_out_no_cost:
            "Built to be taken <strong>before a night out</strong> so recovery stops eating your weekend",
          afternoon_no_crash: "Targets the energy dip behind the <strong>afternoon crash</strong>",
          think_clearly: "Supports the glutathione system behind <strong>mental clarity</strong>",
          liver_numbers_in_range:
            "Feeds your liver what it needs to work toward <strong>numbers back in range</strong>",
          sleep_deep_rested: "Helps your system settle into <strong>deeper, more restorative sleep</strong>",
          feel_like_myself: "Rebuilds the sense of <strong>feeling like you again</strong>, week by week"
        };
        const checkSvg =
          '<svg class="croev-quiz__sr-benefit-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>';
        let html = "";
        this.answerValues("frustrations_recent").forEach((v) => {
          const text = frustrationBenefitMap[v];
          if (text) html += `<div class="croev-quiz__sr-benefit">${checkSvg}<p class="croev-quiz__sr-benefit-text">${text}</p></div>`;
        });
        this.answerValues("life_change_priority").forEach((v) => {
          const text = lifeChangeBenefitMap[v];
          if (text) html += `<div class="croev-quiz__sr-benefit">${checkSvg}<p class="croev-quiz__sr-benefit-text">${text}</p></div>`;
        });
        if (!html) {
          const defaults = [
            "Gives your liver the raw material to make <strong>glutathione</strong>, its master detox compound",
            "Hospital-grade NAC, the <strong>same compound used in IV drips</strong> for liver emergencies",
            "An <strong>MCT oil softgel</strong> with none of the sulfur burps that come with dry powder"
          ];
          defaults.forEach((b) => {
            html += `<div class="croev-quiz__sr-benefit">${checkSvg}<p class="croev-quiz__sr-benefit-text">${b}</p></div>`;
          });
        }
        benefitsEl.innerHTML = html;
      }
    }

    populateSummaryComparison(step) {
      const perceivedAge = this.getPerceivedAge();
      const goalAge = this.getGoalAge();
      const ageAnswer = this.state.answers.age?.value;
      const perceivedAgeEl = step.querySelector('[data-field="perceived_age"]');
      if (perceivedAgeEl && perceivedAge) perceivedAgeEl.textContent = `${perceivedAge} years`;
      const goalAgeEl = step.querySelector('[data-field="goal_age"]');
      if (goalAgeEl && goalAge) goalAgeEl.textContent = `${goalAge} years`;
      const yearsYoungerEl = step.querySelector('[data-field="years_younger"]');
      if (yearsYoungerEl && perceivedAge && goalAge) {
        yearsYoungerEl.textContent = `${perceivedAge - goalAge}+ years`;
      }
      const ageImages = this.config.ageImages || {};
      const nowImageUrl = ageAnswer ? ageImages[ageAnswer] : null;
      if (nowImageUrl && nowImageUrl.length > 0) {
        const nowImg = step.querySelector('[data-image-type="now"]');
        if (nowImg) {
          nowImg.style.backgroundImage = `url('${nowImageUrl}')`;
          const placeholder = nowImg.querySelector(".croev-quiz__comparison-card-placeholder");
          if (placeholder) placeholder.style.display = "none";
        }
        const currentAgeIndex = AGE_ORDER.indexOf(ageAnswer);
        const goalAgeIndex = Math.max(0, currentAgeIndex - 2);
        let goalImageUrl;
        if (currentAgeIndex === 0) goalImageUrl = ageImages.youngest_goal;
        else goalImageUrl = ageImages[AGE_ORDER[goalAgeIndex]];
        const goalImg = step.querySelector('[data-image-type="goal"]');
        if (goalImg && goalImageUrl && goalImageUrl.length > 0) {
          goalImg.style.backgroundImage = `url('${goalImageUrl}')`;
          const placeholder = goalImg.querySelector(".croev-quiz__comparison-card-placeholder");
          if (placeholder) placeholder.style.display = "none";
        }
      }
    }

    populateInfoStep(step) {
      const subheadlineEl = step.querySelector(".croev-quiz__subheadline--dynamic");
      if (subheadlineEl) {
        const template = subheadlineEl.dataset.subheadlineTemplate || subheadlineEl.textContent;
        const problemAreasText = this.getProblemAreasTextBold();
        subheadlineEl.innerHTML = template.replace("{problem_areas}", problemAreasText);
      }
      const testimonialCarousel = step.querySelector("[data-testimonial-carousel]");
      if (testimonialCarousel && !testimonialCarousel.dataset.initialized) {
        const track = testimonialCarousel.querySelector("[data-testimonial-track]");
        const prevBtn = testimonialCarousel.querySelector("[data-testimonial-prev]");
        const nextBtn = testimonialCarousel.querySelector("[data-testimonial-next]");
        if (track && prevBtn && nextBtn) {
          const scrollAmount = () => {
            const card = track.querySelector(".croev-quiz__testimonial-card");
            return card ? card.offsetWidth + 16 : 296;
          };
          prevBtn.addEventListener("click", () => track.scrollBy({ left: -scrollAmount(), behavior: "smooth" }));
          nextBtn.addEventListener("click", () => track.scrollBy({ left: scrollAmount(), behavior: "smooth" }));
        }
        testimonialCarousel.dataset.initialized = "true";
      }
    }

    populateOfferStep(step) {
      const answers = this.state.answers;
      const personalizedEl = step.querySelector("[data-offer-personalized]");
      if (personalizedEl) {
        const concerns = this.getProblemAreasTextBold(3);
        const recommendName =
          (step.getAttribute("data-offer-product-label") || "").trim() || "NAC600 & NAD Daily Complex";
        personalizedEl.innerHTML = `Based on your answers around ${concerns}, we recommend <strong>${recommendName}</strong>.`;
      }

      const benefitsEl = step.querySelector("[data-offer-benefits]");
      if (benefitsEl) {
        const checkSvg =
          '<svg class="croev-quiz__offer-benefit-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>';
        const frustrationOfferMap = {
          two_day_hangovers: "Pre-loads your liver's detox system <strong>before you drink</strong>",
          afternoon_crash: "Supports <strong>steady energy</strong> through the afternoon",
          brain_fog: "Targets the oxidative load behind <strong>brain fog</strong>",
          stubborn_gut: "Supports the system behind a <strong>heavy, stubborn midsection</strong>",
          liver_enzymes: "Supplies the precursor your liver uses to work toward <strong>healthier enzyme levels</strong>",
          fatty_liver: "Feeds your liver the glutathione precursor it relies on under <strong>heavier strain</strong>",
          unrefreshing_sleep: "Helps the body shift toward <strong>deeper, more restorative sleep</strong>",
          not_myself: "Helps you feel <strong>more like yourself</strong> again"
        };
        const lifeChangeOfferMap = {
          night_out_no_cost: "Take it <strong>before a night out</strong> and stop forfeiting your weekend",
          afternoon_no_crash: "Supports <strong>cleaner daytime energy</strong> so afternoons feel doable",
          think_clearly: "Backs the glutathione system behind <strong>sharper thinking</strong>",
          liver_numbers_in_range:
            "Feeds your liver what it needs to push <strong>numbers back toward normal</strong>",
          sleep_deep_rested: "Helps you stack habits that invite <strong>deeper sleep</strong>",
          feel_like_myself: "Helps reclaim that quiet <strong>that is me again</strong> feeling"
        };
        const defaultBenefits = [
          "Hospital-grade NAC, the <strong>same compound used in IV drips</strong> for liver emergencies, no synthetic junk",
          "A full <strong>600mg clinical dose plus NAD+</strong> in one MCT oil softgel",
          "<strong>30-day money-back guarantee</strong>, with under 1% of customers ever claiming it"
        ];

        let benefits = [];
        this.answerValues("frustrations_recent")
          .slice(0, 3)
          .forEach((v) => {
            const b = frustrationOfferMap[v];
            if (b && !benefits.includes(b)) benefits.push(b);
          });
        this.answerValues("life_change_priority")
          .slice(0, 3)
          .forEach((v) => {
            const b = lifeChangeOfferMap[v];
            if (b && !benefits.includes(b)) benefits.push(b);
          });
        if (benefits.length < 3) {
          defaultBenefits.forEach((b) => {
            if (benefits.length < 5 && !benefits.includes(b)) benefits.push(b);
          });
        }
        benefits = benefits.slice(0, 5);

        let html = '<p class="croev-quiz__offer-benefits-title">Why this is the right fit for you</p>';
        benefits.forEach((b) => {
          html += `<div class="croev-quiz__offer-benefit">${checkSvg}<p class="croev-quiz__offer-benefit-text">${b}</p></div>`;
        });
        benefitsEl.innerHTML = html;
      }
    }

    initOfferCarousel(step) {
      const carousel = step.querySelector("[data-offer-carousel]");
      if (!carousel || carousel.dataset.carouselInit) return;
      carousel.dataset.carouselInit = "true";
      const slides = carousel.querySelector("[data-offer-slides]");
      const slideEls = Array.from(slides.children);
      const thumbs = step.querySelectorAll("[data-thumb-index]");
      const prevBtn = carousel.querySelector("[data-carousel-prev]");
      const nextBtn = carousel.querySelector("[data-carousel-next]");
      let current = 0;
      const total = slideEls.length;
      const goTo = (index) => {
        current = Math.max(0, Math.min(index, total - 1));
        slides.style.transform = `translateX(-${current * 100}%)`;
        slideEls.forEach((s, i) => s.classList.toggle("is-active", i === current));
        thumbs.forEach((t, i) => t.classList.toggle("is-active", i === current));
      };
      if (prevBtn) prevBtn.addEventListener("click", () => goTo(current - 1));
      if (nextBtn) nextBtn.addEventListener("click", () => goTo(current + 1));
      thumbs.forEach((thumb) => {
        thumb.addEventListener("click", () => goTo(parseInt(thumb.dataset.thumbIndex, 10)));
      });
      let startX = 0;
      let diffX = 0;
      carousel.addEventListener("touchstart", (e) => { startX = e.touches[0].clientX; diffX = 0; }, { passive: true });
      carousel.addEventListener("touchmove", (e) => { diffX = e.touches[0].clientX - startX; }, { passive: true });
      carousel.addEventListener("touchend", () => {
        if (Math.abs(diffX) > 40) goTo(diffX > 0 ? current - 1 : current + 1);
      });
    }

    getProblemAreasTextBold(limit = 3) {
      const fr = this.answerValues("frustrations_recent");
      return fr.length > 0
        ? "<strong>" +
            fr.slice(0, limit).map((v) => FRUSTRATION_LABELS[v] || v.replace(/_/g, " ")).join(" and ") +
            "</strong>"
        : "<strong>your concerns</strong>";
    }

    handleLoadingStep(step) {
      const duration = parseInt(step.dataset.duration, 10) || 3000;
      const checklist = step.querySelector(".croev-quiz__checklist");
      if (checklist) {
        Array.from(checklist.querySelectorAll(".croev-quiz__checklist-item")).forEach((item, i) => {
          const delay = parseInt(item.dataset.delay, 10) || i * 1000;
          const timer = setTimeout(() => {
            item.classList.add("is-visible");
            setTimeout(() => item.classList.add("is-complete"), 500);
          }, delay);
          this.checklistTimers.push(timer);
        });
      }
      this.autoAdvanceTimer = setTimeout(() => this.nextStep(), duration);
    }

    clearTimers() {
      if (this.autoAdvanceTimer) {
        clearTimeout(this.autoAdvanceTimer);
        this.autoAdvanceTimer = null;
      }
      this.checklistTimers.forEach((t) => clearTimeout(t));
      this.checklistTimers = [];
    }

    handleSkip(btn) {
      const questionId = btn.closest(".croev-quiz__step")?.dataset.questionId;
      if (questionId && this.state.answers[questionId]) {
        delete this.state.answers[questionId];
        this.saveState();
      }
      this.nextStep();
    }

    handleClaimOffer(btn) {
      const productUrl = btn.dataset.productUrl;
      const variantId = btn.dataset.variantId;
      const sellingPlanId = btn.dataset.sellingPlanId;
      const discountCode = btn.closest(".croev-quiz__step")?.dataset.discountCode;
      const params = new URLSearchParams();
      Object.entries(this.state.answers).forEach(([key, value]) => {
        if (Array.isArray(value)) params.set(key, value.map((v) => v.value).join(","));
        else if (value && value.value) params.set(key, value.value);
      });
      if (variantId) params.set("variant", variantId);
      if (sellingPlanId) params.set("selling_plan", sellingPlanId);
      if (discountCode) params.set("discount", discountCode);
      let url = this.redirectUrl || productUrl;
      if (url) {
        const separator = url.includes("?") ? "&" : "?";
        window.location.href = `${url}${separator}${params.toString()}`;
      }
    }

    saveState() {
      try {
        localStorage.setItem(`croev-quiz-${this.sectionId}`, JSON.stringify(this.state));
      } catch (e) {
        console.warn("Could not save quiz state:", e);
      }
    }

    loadState() {
      try {
        const saved = localStorage.getItem(`croev-quiz-${this.sectionId}`);
        if (saved) {
          const parsed = JSON.parse(saved);
          this.state.answers = parsed.answers || {};
        }
      } catch (e) {
        console.warn("Could not load quiz state:", e);
      }
    }

    clearState() {
      try {
        localStorage.removeItem(`croev-quiz-${this.sectionId}`);
      } catch (e) {
        console.warn("Could not clear quiz state:", e);
      }
    }

    capitalize(str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }

    destroy() {
      this.clearTimers();
    }
  }

  function initQuizzes() {
    document.querySelectorAll('[data-section-type="croev-quiz"]').forEach((container) => {
      if (!container.croevQuiz) container.croevQuiz = new CroevQuiz(container);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initQuizzes);
  } else {
    initQuizzes();
  }

  document.addEventListener("shopify:section:load", (e) => {
    const container = e.target.querySelector('[data-section-type="croev-quiz"]');
    if (container) container.croevQuiz = new CroevQuiz(container);
  });

  document.addEventListener("shopify:section:unload", (e) => {
    const container = e.target.querySelector('[data-section-type="croev-quiz"]');
    if (container && container.croevQuiz) container.croevQuiz.destroy();
  });
})();